import { LastContent } from '../LastContent/LastContent.component';
import Head from 'next/head';
import React, { ComponentPropsWithRef, FC, useContext, useEffect } from 'react';
import { BreadCrumb } from '@components/BreadCrumb/BreadCrumb.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { ClippedImageLoader } from '@components/ClippedImage/ClippedImage.component';
import { PlcImgLoader } from '@components/Loaders/PlcImgLoader.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { PlcMessageWithRetry } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { SocialShareBar } from '@components/SocialShare/SocialShareBar.component';
import { buildMetaTags, ShareProps } from '@components/SocialShare/SocialShareButton.component';
import { Text } from '@components/Text/Text.component';
import { TextPropsOptionalVariant } from '@components/Text/Text.types';
import { MetasProps, PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { Spacing } from '@styles/Spacing.style';
import { ClippedImageStyle } from '@utils/images';
import { addScript } from '@utils/layout.utils';
import classnames from 'classnames';
import { GeneralContext } from 'features/business/app.contexts';
import { PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

import './ContentLayout.scss';

type Props = ComponentPropsWithRef<'div'> &
  Pick<ShareProps, 'description' | 'imgAlt' | 'imgUrl'> & {
    articleTitle: string;
    // We need to pass articleUrl and not infer it from the router because of ssg (otherwise we have the json url) (PLC-1339)
    articleUrl: string;
    titleTextProps?: TextPropsOptionalVariant;
    breadcrumbSteps: BreadCrumbStep[];
    preTitleComponent?: JSX.Element;
    postTitleComponent?: JSX.Element;
    illustrationComponent?: JSX.Element;
    gridReadAlsoArticle?: JSX.Element;
    withSocialShare?: boolean;
    gridRightComponent?: JSX.Element;
    gridRightMobileEndComponent?: JSX.Element;
    gridBottomComponent?: JSX.Element;
    gridInfosComponent?: JSX.Element;
    metas: MetasProps;
    tagProps?: PlcTagProps;
    breakpoint?: Breakpoint;
  };

const ContentLayout: FC<Props> = ({
  articleUrl,
  articleTitle,
  titleTextProps = {},
  breadcrumbSteps,
  preTitleComponent,
  postTitleComponent,
  illustrationComponent,
  withSocialShare = true,
  gridRightComponent,
  gridRightMobileEndComponent,
  gridBottomComponent,
  gridReadAlsoArticle,
  gridInfosComponent,
  metas,
  className,
  children,
  tagProps,
  imgAlt,
  imgUrl,
  description,
  breakpoint = Breakpoint.m,
  ...rest
}) => {
  const isMobile = useIsBreakpointDown(breakpoint);
  const isLargeDesktop = useIsBreakpointDown(1440 as Breakpoint);

  const rootClass = classnames('content-layout', 'plc-page', isMobile === false && 'desktop-layout', className);
  const postTitleClassName = useClassNames({
    all: ['post-title', 'inline-flex', 'column'],
    m: ['row', 'wrap', 'align-center'],
  });
  const infosClassName = useClassNames({
    all: ['grid-infos', gridInfosComponent ? 'plc-mb-basis' : undefined],
    m: 'plc-mb-0',
  });
  const displayLeft = !isLargeDesktop && withSocialShare;
  const leftClassName = useClassNames({ all: ['grid-left', displayLeft ? 'plc-mx-m' : undefined] });
  const bottomClassName = useClassNames({
    all: [
      'grid-bottom',
      gridBottomComponent ? 'plc-pt-l' : undefined,
      gridRightMobileEndComponent ? 'plc-mb-l' : undefined,
    ],
  });

  const readAlsoArticleClassName = useClassNames({
    all: ['grid-readAlsoArticle'],
  });

  useEffect(() => {
    // Pas de pose des cookies twitter avant consentement
    if (!window || !window.CookieConsent || !window.CookieConsent.consent.marketing) {
      return;
    }
    addScript(
      'https://platform.twitter.com/widgets.js',
      [
        { attribute: 'async', value: 'true' },
        { attribute: 'data-cookieconsent', value: 'marketing' },
      ],
      { target: document.getElementsByClassName('twitter-embed')[0] }
    );
  }, []);

  const { tag: titleTag, variant: titleVariant, ...restTitleTextProps } = titleTextProps;

  const { host } = useContext(GeneralContext);

  return (
    <>
      <Head>{buildMetaTags({ description, imgAlt, imgUrl, title: articleTitle, url: `${host}${articleUrl}` })}</Head>
      <div className={rootClass} {...rest}>
        {displayLeft && (
          <aside className={leftClassName}>
            <SocialShareBar orientation="vertical" />
          </aside>
        )}
        <header className="grid-title plc-mb-basis plc-mt-basis plc-mt-up-m-0">
          <BreadCrumb steps={breadcrumbSteps} />
          {preTitleComponent && (
            <div className="pre-title flex space-between align-start plc-mb-basis">
              {preTitleComponent}
              {isLargeDesktop && withSocialShare && <SocialShareBar />}
            </div>
          )}
          <Text
            className="plc-mb-basis"
            tag={titleTag || 'h1'}
            variant={titleVariant || isMobile ? 'h3' : 'h2'}
            {...restTitleTextProps}
          >
            {articleTitle}
          </Text>
          {postTitleComponent && <div className={postTitleClassName}>{postTitleComponent}</div>}
        </header>
        <PlcPageLayout className="grid-main plc-mb-0" tagProps={tagProps} {...metas}>
          {children}
        </PlcPageLayout>
        {gridBottomComponent && <aside className={bottomClassName}>{gridBottomComponent}</aside>}
        {gridReadAlsoArticle && <aside className={readAlsoArticleClassName}>{gridReadAlsoArticle}</aside>}
        {illustrationComponent && <aside className="grid-illu">{illustrationComponent}</aside>}
        {gridInfosComponent && <aside className={infosClassName}>{gridInfosComponent}</aside>}
        {gridRightComponent && (
          <aside className="grid-right plc-pt-l plc-mb-l  plc-mb-up-m-0">{gridRightComponent}</aside>
        )}
        {gridRightMobileEndComponent && (
          <aside className="grid-right-mobile-end plc-pt-l plc-mb-l  plc-mb-up-m-0">
            {gridRightMobileEndComponent}
          </aside>
        )}
      </div>
      <div className="twitter-embed" />
    </>
  );
};

const ContentLayoutLoaderOrError: FC<ComponentPropsWithRef<'div'> & {
  isLoading?: boolean;
  retryCallback?: () => void;
  withIllu: boolean;
  breakpoint?: Breakpoint;
}> = ({ isLoading, retryCallback, className, children, withIllu, breakpoint = Breakpoint.m, ...rest }) => {
  const isMobile = useIsBreakpointDown(breakpoint);

  const rootClass = classnames('content-layout', 'plc-page', isMobile === false && 'desktop-layout', className);

  return (
    <div className={rootClass} {...rest}>
      <header className="grid-title plc-mb-basis">
        <PlcTextListLoader
          style={{ paddingTop: Spacing.xl }}
          textProps={[
            { bottomSpacing: Spacing.m, variant: 'h6', width: 130 },
            { bottomSpacing: Spacing.m, numberOfLine: 2, variant: 'h2', width: 600 },
            { variant: 'h6', width: 260 },
          ]}
          uniqueKey="err-contentLayout-title"
        />
      </header>
      <main className="grid-main">
        {isLoading ? (
          <>
            <PlcTextListLoader
              style={{ paddingTop: Spacing.xl }}
              textProps={[
                { bottomSpacing: Spacing.basis, numberOfLine: 3, variant: 'h4', width: 600 },
                { bottomSpacing: Spacing.m, numberOfLine: 5, variant: 'p', width: 600 },
              ]}
              uniqueKey="err-contentLayout-main"
            />
            <PlcImgLoader height={500} uniqueKey="err-contentLayout-main-img" width={400} />
            <PlcTextListLoader
              textProps={[{ bottomSpacing: Spacing.xl, numberOfLine: 7, variant: 'p', width: 600 }]}
              uniqueKey="err-contentLayout-main-txt"
            />
          </>
        ) : (
          <PlcMessageWithRetry retryCallback={retryCallback} />
        )}
      </main>
      {withIllu && (
        <aside className="grid-illu plc-mb-basis plc-mb-up-m-0">
          {isLoading && (
            <ClippedImageLoader clipStyle={ClippedImageStyle.contentAside} uniqueKey="err-contentLayout-illu" />
          )}
        </aside>
      )}
      <aside className="grid-right plc-pt-l plc-mb-l  plc-mb-up-m-0">
        <LastContent cards={[]} isValidating={true} />
      </aside>
    </div>
  );
};

export { ContentLayout, ContentLayoutLoaderOrError };
